import type { RefObject } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { BodyText, IconLink, LoadingPlaceholder, theme } from '@sg/garnish';

import { useLocalizationContext } from '@order/Localization';

import {
  LOCATION_RESULTS_CARD_HEIGHT,
  LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL,
  LOCATION_RESULTS_CARD_HEIGHT_WITHOUT_PADDING,
} from '../../LocationResultsV2.constants';
import type {
  Location,
  OnSelectedLocationCardPress,
} from './LocationResultCard.types';
import {
  LocationResultCardDetailsModal,
  LocationResultCardImg,
} from './subcomponents';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsCard = (props: LocationResultsCardProps) => {
  const {
    location,
    withTopBorder,
    locationSearchType,
    withDetailsModal,
    isLoading = false,
    isDisabled = false,
    onPress,
    onLayout,
    onUmount,
  } = props;
  const {
    id,
    name,
    address,
    city,
    state,
    zipCode,
    imageUrl,
    estimatedDeliveryTime,
    restaurantSlug,
  } = location;

  const { t } = useLocalizationContext();
  const isVisible = useIsFocused();

  // ─── State ───────────────────────────────────────────────────────────

  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);

  const cardRef = useRef<TouchableOpacity>(null);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const toggleDetailsModal = useCallback(() => {
    setIsDetailsModalVisible((currentValue) => !currentValue);
  }, []);

  const handleOnLayout = useCallback(() => {
    onLayout?.(cardRef, id);
  }, [id, onLayout]);

  const handleOnPress = useCallback(() => {
    onPress({ restaurantId: id, restaurantSlug, restaurantName: name });
  }, [id, name, restaurantSlug, onPress]);

  const handleOnUnmount = useCallback(() => {
    onUmount?.(id);
  }, [id, onUmount]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const cardStyle = [
    styles.card,
    withTopBorder ? styles.cardWithBorder : undefined,
    isDisabled ? styles.cardDisabled : undefined,
  ];

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => handleOnUnmount, [handleOnUnmount]);

  // ─────────────────────────────────────────────────────────────────────

  const shouldDisplayDetailsModal = Boolean(
    withDetailsModal && isDetailsModalVisible && isVisible,
  );
  const shouldDisableControls = isDisabled || isLoading;
  const hasAddressDetails = Boolean(city && state && zipCode);

  const addressDetails = hasAddressDetails
    ? `${city}, ${state} ${zipCode}`
    : undefined;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <TouchableOpacity
        ref={cardRef}
        style={cardStyle}
        accessibilityRole="button"
        disabled={shouldDisableControls}
        onLayout={handleOnLayout}
        onPress={handleOnPress}
        accessibilityState={{ disabled: isDisabled, busy: isLoading }}
      >
        <View style={styles.innerContainer}>
          {/* ─── Main Info ─────────────────────────────────────────────────────── */}

          <LocationResultCardImg
            locationName={name}
            locationSearchType={locationSearchType}
            imageUrl={imageUrl}
          />

          <View style={styles.infoContainer}>
            <BodyText
              bold
              size={2}
              numberOfLines={1}
              style={styles.heading}
              accessibilityLabel={name}
            >
              {name}
            </BodyText>

            <BodyText numberOfLines={1} accessibilityLabel={address}>
              {address}
            </BodyText>

            {addressDetails ? (
              <BodyText numberOfLines={1} accessibilityLabel={addressDetails}>
                {addressDetails}
              </BodyText>
            ) : null}

            {estimatedDeliveryTime ? (
              <BodyText
                numberOfLines={1}
                accessibilityLabel={estimatedDeliveryTime}
              >
                {t('delivery.estimate', { estimate: estimatedDeliveryTime })}
              </BodyText>
            ) : null}
          </View>

          {/* ─── Additional Details ────────────────────────────────────────────── */}

          {withDetailsModal ? (
            <View style={styles.detailsBtnContainer}>
              <IconLink
                name="IconBadgeInfo"
                onPress={toggleDetailsModal}
                disabled={shouldDisableControls}
                hitSlop={theme.spacing.xsmall}
              />
            </View>
          ) : null}
        </View>
      </TouchableOpacity>

      <LocationResultCardDetailsModal
        visible={shouldDisplayDetailsModal}
        location={location}
        locationSearchType={locationSearchType}
        addressDetails={addressDetails}
        isLoading={isLoading}
        isDisabled={isDisabled}
        onRequestClose={toggleDetailsModal}
        onOrderNowBtnPress={onPress}
      />
    </>
  );
};

// ─── Variations ──────────────────────────────────────────────────────────────

export const LocationResultCardLoading = (
  props: Pick<LocationResultsCardProps, 'withTopBorder'>,
) => {
  const { withTopBorder } = props;

  const cardStyle = [
    styles.card,
    withTopBorder ? styles.cardWithBorder : undefined,
  ];

  return (
    <View style={cardStyle}>
      <LoadingPlaceholder
        rows={1}
        columns={1}
        rowHeight={LOCATION_RESULTS_CARD_HEIGHT_WITHOUT_PADDING}
        borderRadius={theme.spacing.small}
        palette="cream"
      />
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  card: {
    height: LOCATION_RESULTS_CARD_HEIGHT,
    paddingVertical: LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL,
  },
  cardWithBorder: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.LIGHT,
  },
  cardDisabled: {
    opacity: 0.5,
  },
  innerContainer: {
    flexDirection: 'row',
  },
  heading: {
    marginBottom: 2,
  },
  infoContainer: {
    flex: 1,
  },
  detailsBtnContainer: {
    marginLeft: 12,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsCardProps = {
  readonly location: Location;
  readonly withTopBorder?: boolean;
  readonly withDetailsModal?: boolean;
  readonly locationSearchType: 'pickup' | 'delivery' | 'outpost';
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
  readonly onLayout?: (
    listItemRef: RefObject<View>,
    listItemId: string,
  ) => void;
  readonly onPress: OnSelectedLocationCardPress;
  readonly onUmount?: (id: Location['id']) => void;
};
